// Search Enrichment Types
export interface SearchEnrichmentSource {
  title: string;
  url: string;
}

export interface SearchEnrichmentInsight {
  id: string;
  type: 'action' | 'clarification';
  content: string;
  source: SearchEnrichmentSource;
}

// Search Enrichment Debug Types (Dev Mode Only)
export interface TavilySearchResult {
  title: string;
  url: string;
  content: string;
  score: number;
  published_date?: string;
}

export interface SearchResultDebugInfo {
  query: string;
  start_date?: string;
  end_date?: string;
  answer?: string;
  results: TavilySearchResult[];
  result_count: number;
}

export interface SearchEnrichmentDebugInfo {
  search_queries: Array<{
    query: string;
    start_date?: string;
    end_date?: string;
  }>;
  search_results: SearchResultDebugInfo[];
  collated_context: string;
  researcher_model?: string;
  reporter_model?: string;
  execution_time_seconds?: number;
  query_generation_time_seconds?: number;
  search_execution_time_seconds?: number;
  collation_time_seconds?: number;
  reporter_time_seconds?: number;
}

export type ModelSelection = 'gpt-4.1' | 'gpt-5.6';

export const DEFAULT_MODEL_SELECTION: ModelSelection = 'gpt-5.6';

export const MODEL_OPTIONS: Array<{
  value: ModelSelection;
  label: string;
  subtitle: string;
}> = [
  {
    value: 'gpt-5.6',
    label: 'GPT-5.6 Orchestration',
    subtitle: 'Thinks harder, more insightful',
  },
  {
    value: 'gpt-4.1',
    label: 'GPT-4.1',
    subtitle: 'Fast + Detailed + Simple',
  },
];

// Message Types
export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean; // Indicates if this message is currently being streamed
  metadata?: {
    sqlQuery?: string;
    tableData?: any[];
    tableTitle?: string;  // Title for master SQL results
    visualization?: ChartConfig;
    visualizationData?: any; // Legacy field, deprecated
    requiresClarification?: boolean;
    clarificationQuestions?: string[];
    // Citation support
    citationDescriptions?: { [citationId: number]: string | { description: string; columns_used: string[]; title?: string } };
    citationMetadata?: { [key: string]: any }; // Raw citation metadata from backend
    citationStatus?: 'pending' | 'completed' | 'failed'; // Status of citation processing
    // Search Enrichment support
    searchEnrichment?: SearchEnrichmentInsight[];
    searchEnrichmentDebug?: SearchEnrichmentDebugInfo; // Dev mode debug info
    // Feedback support
    jobId?: string;
    feedbackRating?: boolean; // true=thumbs up, false=thumbs down, undefined=not rated
    model?: ModelSelection;
  };
  followUps?: string[];
  // Context information for follow-up tracking
  isFollowUp?: boolean;
  contextMessageId?: string; // ID of the message this is following up on
}

// Conversation Types
export interface MessageThinkingState {
  messageId: string;
  userMessageId: string; // The user message this thinking relates to
  isActive: boolean; // Whether this thinking state should receive live updates
  isExpanded: boolean;
  hasShownDetailedThinking: boolean;
  requirementHistory: string[];
  trackedPlans: string[]; // Convert Set to Array for serialization
  // Frozen final state for completed thinking
  finalStage?: string;
  finalStageMessage?: string;
  finalProgress?: number;
}

export interface Conversation {
  id: string;
  title: string;
  timestamp: Date;
  messages: Message[];
  lastMessagePreview?: string;
  thinkingStates?: { [key: string]: MessageThinkingState }; // Add thinking states to conversation
}

// Chat Context Types
export interface ChatContextType {
  messages: Message[];
  isLoading: boolean;
  apiError: Error | null;
  conversations: Conversation[];
  currentConversationId: string | null;
  currentStage?: string;
  stageMessage?: string;
  progress?: number;
  pendingClarification?: {
    originalQuery: string;
    questions: string[];
  } | null;
  storageWarning?: {
    show: boolean;
    message: string;
    canStore: boolean;
  } | null;
  sendMessage: (content: string, options?: {
    isFollowUp?: boolean;
    contextMessageId?: string;
    model?: ModelSelection;
  }) => Promise<void>;
  stopMessage: () => void;
  clearMessages: () => void;
  enableMockMode: () => void;
  retryConnection: () => void;
  startNewConversation: () => string | null;
  loadConversation: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => void;
  saveCurrentConversation: (messagesToSave?: Message[], conversationIdOverride?: string) => void;
  // Storage management functions
  clearStorageWarning: () => void;
  getStorageInfo: () => any;
  forceCleanupOldConversations: () => { success: boolean; deletedCount: number; keptCount: number };
  exportConversationsAsJSON: () => void;
  importConversationsFromJSON: (file: File) => Promise<{ success: boolean; message: string; imported?: number }>;
  // UI state
  isSidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  // Dev feature: Search enrichment toggle
  searchEnrichmentEnabled: boolean;
  setSearchEnrichmentEnabled: (enabled: boolean) => void;
  // Per-conversation loading check (with staleness guard)
  isConversationInProgress: (conversationId: string) => boolean;
  // Feedback: update metadata on a specific message and persist
  updateMessageMetadata: (messageId: string, metadataUpdate: Record<string, any>) => void;
}

// API Types
export interface ChatRequest {
  message: string;
  conversationId?: string;
  previous_user_message?: string;
  previous_assistant_response?: string;
  original_query?: string;  // For clarification responses
  is_clarification?: boolean;  // Flag to indicate this is a clarification response
  user_email?: string;  // For user database logging (optional)
  enable_search_enrichment?: boolean;  // Dev toggle: enable/disable search enrichment (default false)
  model_selection?: ModelSelection;
}

export interface ChatResponse {
  response?: string;
  sqlQuery?: string;
  tableData?: any[];
  followUpSuggestions?: string[];
  requires_clarification?: boolean;
  clarification_questions?: string[];
  error?: string;
  stage?: string;
  metadata?: any;
  visualization?: ChartConfig;
  // Citation support
  cited_response?: string;
  citation_descriptions?: { [citationId: number]: string | { description: string; columns_used: string[]; title?: string } };
  citation_metadata?: { [key: string]: any }; // Raw citation metadata from backend
  // Search Enrichment support
  search_enrichment?: SearchEnrichmentInsight[];
  search_enrichment_debug?: SearchEnrichmentDebugInfo; // Dev mode debug info
}

export interface PlotlyGraphJSON {
  data: any[] | string; // Can be array (parsed) or string (from backend)
  layout: any;
  config?: any;
}

export interface ChartConfig {
  type: 'bar' | 'line' | 'pie' | 'area' | 'scatter' | 'table' | 'none';
  title: string;
  // Support both direct GraphJSON and separate data/layout
  graphJSON?: PlotlyGraphJSON;
  // Legacy support for data array (will be converted to GraphJSON)
  data?: any[];
  x_axis?: string;
  y_axis?: string;
  color_key?: string;
  config?: any;
  // Optional Python code that generates the GraphJSON
  pythonCode?: string;
}

export interface VisualizationSpec {
  charts: ChartConfig[];
  primary_chart: number;
  description: string;
  data_summary: any;
}

// Feedback Types
export interface GranularFeedbackItem {
  rating: boolean | null;
  comment?: string;
}

export interface FeedbackPayload {
  job_id: string;
  conversation_id?: string;
  user_email: string;
  overall_rating: boolean;
  overall_comment?: string;
  granular_feedback?: Record<string, GranularFeedbackItem>;
}
